// Mandanten-Liste mit Anlegen + Bearbeiten.
//
// Massen-Import bleibt absichtlich im CLI-Skript
// `app/cli/import_mandanten.py` — fuer eine 5000-Zeilen-CSV ist eine
// Web-Form das falsche Werkzeug.

import { useState } from "react";

import { useMandanten } from "@/api/hooks";
import type { Mandant } from "@/api/types";
import { BulkInviteModal } from "@/components/BulkInviteModal";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { InvitationModal } from "@/components/InvitationModal";
import { Loader } from "@/components/Loader";
import { MandantForm } from "@/components/MandantForm";
import { PageHeader } from "@/components/PageHeader";

import "./MandantenPage.css";

export function MandantenPage() {
  const [aktivOnly, setAktivOnly] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editing, setEditing] = useState<Mandant | null>(null);
  const [inviting, setInviting] = useState<Mandant | null>(null);

  const mandanten = useMandanten(aktivOnly ? true : undefined);

  return (
    <>
      <PageHeader
        title="Mandanten"
        subtitle="BMD-Stammdaten. Massen-Import läuft über das CLI-Skript."
        action={
          <div className="bmd-row" style={{ gap: 12 }}>
            <label className="bmd-mandanten-toggle">
              <input
                type="checkbox"
                checked={aktivOnly}
                onChange={(e) => setAktivOnly(e.target.checked)}
              />
              <span>Nur aktive</span>
            </label>
            <Button variant="ghost" size="sm" onClick={() => setShowBulk(true)}>
              Per CSV einladen
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
              + Neuer Mandant
            </Button>
          </div>
        }
      />

      {mandanten.isLoading ? (
        <Loader />
      ) : mandanten.isError ? (
        <div className="bmd-card bmd-mandanten-error">
          Mandanten konnten nicht geladen werden: {mandanten.error.message}
        </div>
      ) : (mandanten.data?.length ?? 0) === 0 ? (
        <EmptyState
          title="Noch keine Mandanten"
          description={
            aktivOnly
              ? 'Keine aktiven Mandanten. Toggle "Nur aktive" ausschalten oder neuen anlegen.'
              : "Lege deinen ersten Mandanten an — oder importiere via CLI eine BMD-CSV."
          }
          action={
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              Mandant anlegen
            </Button>
          }
        />
      ) : (
        <table className="bmd-table bmd-mandanten-table">
          <thead>
            <tr>
              <th style={{ width: "10%" }}>Kundennr.</th>
              <th>Name</th>
              <th>Steuernr. FA</th>
              <th>Ort</th>
              <th style={{ width: "10%" }}>Status</th>
              <th style={{ width: "88px" }} aria-label="Aktionen" />
            </tr>
          </thead>
          <tbody>
            {mandanten.data!.map((m) => (
              <tr key={m.id} onClick={() => setEditing(m)}>
                <td className="bmd-mono">{m.bmd_kundennr}</td>
                <td>
                  <div style={{ fontWeight: 500 }}>
                    {m.vorname ? `${m.vorname} ${m.name}` : m.name}
                  </div>
                  {m.name_varianten.length > 0 && (
                    <div className="bmd-subtle" style={{ fontSize: 12 }}>
                      {m.name_varianten.join(" · ")}
                    </div>
                  )}
                </td>
                <td className="bmd-mono bmd-subtle">{m.steuernr_fa ?? "—"}</td>
                <td className="bmd-subtle">
                  {m.ort ? (m.plz ? `${m.plz} ${m.ort}` : m.ort) : "—"}
                </td>
                <td>
                  {m.aktiv ? (
                    <span className="bmd-chip bmd-mandant-active">aktiv</span>
                  ) : (
                    <span className="bmd-chip">inaktiv</span>
                  )}
                </td>
                <td>
                  <div
                    className="bmd-row"
                    style={{ gap: 4, justifyContent: "flex-end" }}
                  >
                    <button
                      type="button"
                      className="bmd-rowaction"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInviting(m);
                      }}
                      title="Klient einladen"
                      aria-label="Klient einladen"
                    >
                      <InviteIcon />
                    </button>
                    <button
                      type="button"
                      className="bmd-rowaction"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing(m);
                      }}
                      title="Bearbeiten"
                      aria-label="Mandant bearbeiten"
                    >
                      <EditIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && <MandantForm onClose={() => setShowCreate(false)} />}
      {showBulk && <BulkInviteModal onClose={() => setShowBulk(false)} />}
      {editing && (
        <MandantForm mandant={editing} onClose={() => setEditing(null)} />
      )}
      {inviting && (
        <InvitationModal
          mandant={inviting}
          onClose={() => setInviting(null)}
        />
      )}
    </>
  );
}

function InviteIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}
